import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';
import { ArrowLeft } from 'lucide-react';

// Photo collage URL
const WORKSHOP_COLLAGE = "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/4pfcj09h_Polish_20260323_094215771.png";

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-white" data-testid="workshops-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link 
            to="/giving-back" 
            className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-medium mb-8 hover:gap-3 transition-all"
          >
            <ArrowLeft size={18} />
            Back to #GivingBack
          </Link>
          
          <span className="inline-block px-3 py-1 bg-[#faf9f7] text-[#d4782c] font-dm-sans font-semibold text-sm mb-4">
            2023
          </span>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-4">
            Training Workshops
          </h1>
          <p className="text-xl md:text-2xl text-[#d4782c] font-dm-sans font-medium">
            #GivingBack 2023
          </p>
        </div>
      </div>
    </section>
  );
};

// Content Section
const ContentSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-[#faf9f7]" data-testid="workshops-content">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="space-y-6 text-[#4a4744] leading-relaxed">
            <p>
              In 2023, Agilitie ran a series of free training workshops designed for delivery leads, iteration managers, project managers, and agile coaches working across enterprise environments.
            </p>
            <p>
              The sessions focused on practical, foundational topics: better ways of working, the fundamentals of flow, smaller batch sizing, estimation techniques, and iterative development. The aim was simple: to give practitioners the tools and frameworks to improve how their teams deliver, without the price tag that usually comes with that kind of guidance.
            </p>
            <p>
              It was the initiative that started it all, and it established the principle that has guided every #GivingBack program since: share what you know, with anyone willing to learn.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Photo Gallery Section
const PhotoGallerySection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-white" data-testid="workshops-gallery">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-8">
            From the Workshops
          </h2>
          
          {/* Photo collage */}
          <div className="border border-[#e5e2dc] overflow-hidden">
            <img 
              src={WORKSHOP_COLLAGE}
              alt="Photos from the 2023 #GivingBack training workshops"
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-20 bg-[#faf9f7]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#4a4744] mb-6">
            Interested in future #GivingBack initiatives?
          </p>
          <Link 
            to="/giving-back#get-involved"
            className="inline-block bg-[#1a1a1a] text-white px-8 py-4 font-dm-sans font-semibold hover:bg-[#333] transition-colors"
          >
            Get Involved
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function WorkshopsPage() {
  return (
    <Layout>
      <SEO 
        title="Training Workshops | #GivingBack 2023"
        path="/giving-back/workshops-2023"
        description="In 2023, Agilitie ran free training workshops for delivery leads, iteration managers, project managers, and agile coaches on flow fundamentals, batch sizing, and iterative development."
      />
      <HeroSection />
      <ContentSection />
      <PhotoGallerySection />
      <CTASection />
    </Layout>
  );
}
